import { useState } from 'react';
import { AnimatePresence } from 'motion/react';
import { RENDER_KINDS, RENDER_LABELS, type RenderKind } from '../lib/deck';
import { formatCode } from '../lib/format';
import type { RenderUrls } from '../lib/api';
import { PlaceholderRender } from './PlaceholderRender';
import { Lightbox } from './Lightbox';

interface DeckImageGridItem {
  layoutCode: string;
  selectedRenders: string[];
}

/**
 * The combined "Selected images" grid for a deck: flattens each layout's selected
 * renders into one grid, click to expand (Lightbox with prev/next). When
 * `onRemoveImage` is given each tile gets an X to drop that image; omit it for a
 * read-only view (e.g. a saved deck's detail page).
 */
export function DeckImageGrid({
  items,
  rendersByCode,
  onRemoveImage,
}: {
  items: DeckImageGridItem[];
  rendersByCode: Record<string, Record<string, RenderUrls>>;
  onRemoveImage?: (layoutCode: string, kind: RenderKind) => void;
}) {
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);

  const images = items.flatMap((item) =>
    RENDER_KINDS.filter((kind) => item.selectedRenders.includes(kind)).map((kind) => ({
      layoutCode: item.layoutCode,
      kind,
      label: RENDER_LABELS[kind],
      thumbUrl: rendersByCode[item.layoutCode]?.[kind]?.thumbUrl ?? null,
      fullUrl: rendersByCode[item.layoutCode]?.[kind]?.fullUrl ?? null,
    })),
  );

  const active = lightboxIdx !== null ? images[lightboxIdx] : null;

  return (
    <div className="rounded-2xl border border-vari-soft-gray bg-white overflow-hidden">
      <div className="p-5 sm:p-6 space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-[11px] uppercase tracking-[0.25em] text-vari-blue-accessible">Selected images</p>
          <p className="text-xs text-vari-gray">
            {images.length} image{images.length === 1 ? '' : 's'}
          </p>
        </div>

        {images.length === 0 ? (
          <p className="text-sm text-vari-gray">No images selected.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {images.map((img, idx) => (
              <div key={`${img.layoutCode}:${img.kind}`} className="space-y-1.5">
                <div className="relative rounded-md overflow-hidden border-2 border-vari-navy">
                  <button
                    type="button"
                    onClick={() => setLightboxIdx(idx)}
                    className="block w-full cursor-pointer"
                    title={`View #${formatCode(img.layoutCode)} · ${img.label}`}
                  >
                    <PlaceholderRender
                      variant="card"
                      label={img.label}
                      src={img.thumbUrl}
                      fit={img.kind === 'dimensional' ? 'contain' : 'cover'}
                    />
                  </button>
                  {onRemoveImage && (
                    <button
                      type="button"
                      onClick={() => onRemoveImage(img.layoutCode, img.kind)}
                      aria-label={`Remove ${img.label} from #${formatCode(img.layoutCode)}`}
                      title="Remove this image"
                      className="absolute top-1.5 right-1.5 w-7 h-7 rounded-md bg-white/90 border-2 border-vari-soft-gray text-vari-navy hover:bg-vari-red hover:border-vari-red hover:text-white flex items-center justify-center shadow transition-colors"
                    >
                      <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="currentColor">
                        <path d="M4.293 4.293a1 1 0 011.414 0L8 6.586l2.293-2.293a1 1 0 111.414 1.414L9.414 8l2.293 2.293a1 1 0 01-1.414 1.414L8 9.414l-2.293 2.293a1 1 0 01-1.414-1.414L6.586 8 4.293 5.707a1 1 0 010-1.414z" />
                      </svg>
                    </button>
                  )}
                </div>
                <p className="text-[10px] uppercase tracking-[0.15em] text-vari-gray truncate">
                  #{formatCode(img.layoutCode)} · {img.label}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {active && (
          <Lightbox
            src={active.fullUrl}
            label={`#${formatCode(active.layoutCode)} · ${active.label}`}
            onClose={() => setLightboxIdx(null)}
            onPrev={
              images.length > 1
                ? () => setLightboxIdx((i) => (i === null ? i : (i - 1 + images.length) % images.length))
                : undefined
            }
            onNext={
              images.length > 1
                ? () => setLightboxIdx((i) => (i === null ? i : (i + 1) % images.length))
                : undefined
            }
          />
        )}
      </AnimatePresence>
    </div>
  );
}
